import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { extractVideoId } from '../../lib/youtube';
import { useLocale } from '../../lib/i18n';
import { useMagneticHover } from '../../hooks/useMagneticHover';
import { HeroPackPreview } from './HeroPackPreview';
import { BrandMark } from '../BrandMark';
import { track, Events } from '../../lib/analytics';
import { isPlaylistUrl, TRIGGERS } from '../../lib/conversionTriggers';
import { ConversionChip } from '../ConversionChip';

/**
 * Hero — the entry point. Tightened above-the-fold so the headline,
 * sub-claim, paste-field and CTAs all live in the first viewport on
 * a typical desktop (≥768px tall).
 *
 * Composition:
 *   • Medallion seal (small, dignified — not a hero ornament that
 *     wastes space)
 *   • Eyebrow → Headline → Gold draw-rule
 *   • Sub-claim (one sentence, concrete promise)
 *   • Paste input + primary CTA in one row
 *   • Trust note ("free, no card") immediately under the input
 *   • Tertiary links: "see how" and "sample pack"
 *   • Live Pack preview on the right (HeroPackPreview)
 */
export function Hero() {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const ctaRef = useMagneticHover<HTMLButtonElement>(0.22);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const id = extractVideoId(value);
    if (!id) {
      setError(t.invalidUrl);
      return;
    }
    // Funnel: visitor pasted a valid YouTube URL on the Hero CTA.
    // Top-of-funnel — no auth, no user context, just intent.
    track(Events.PASTE_URL, { locale, source: 'hero' });
    navigate(`/new?v=${id}`);
  }

  function scrollToHow() {
    document.querySelector('#how')?.scrollIntoView({ behavior: 'smooth' });
  }

  const headlineWords = t.heroHeadline.split(' ');

  return (
    <section className="relative overflow-hidden bg-creme paper">
      {/* Ambient hero video — dark navy + gold particles. `mix-blend-screen`
          makes the navy background transparent, so only the warm gold-line
          pulse and drifting amber bokeh ghost through onto the creme paper.
          Reads as "live light on the page" rather than a competing visual.
          Hidden for prefers-reduced-motion, and behind the existing radial
          gradient so the brand chiaroscuro layering is preserved. */}
      <video
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-screen motion-reduce:hidden"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden
      >
        <source src="/hero-loop.mp4" type="video/mp4" />
      </video>

      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[75vh] opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at top right, rgba(201,162,75,0.16), transparent 55%), radial-gradient(ellipse at bottom left, rgba(10,26,58,0.06), transparent 50%)',
        }}
        aria-hidden
      />

      {/* Tighter vertical rhythm on mobile so the headline + CTA land
          above the first-fold on a 375 × 812 iPhone. The XL monogram
          is hidden on small screens for the same reason — it pushes
          the actual value-prop below the fold otherwise. */}
      <div className="relative mx-auto max-w-6xl px-5 py-4 sm:px-8 sm:py-14 lg:py-16">
        <div className="grid items-start gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-14">
          {/* Left — copy */}
          <div>
            <div className="animate-fade-in">
              <div className="hidden sm:block">
                <BrandMark variant="monogram" size="xl" tone="navy" decorative />
              </div>
              <div className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold-deep sm:mt-4">
                {t.heroEyebrow}
              </div>
            </div>

            <h1
              className="word-stagger mt-3 font-serif text-4xl leading-[1.05] text-navy sm:mt-5 sm:text-5xl lg:text-6xl"
            >
              {headlineWords.map((word, i) => (
                <span key={i} style={{ ['--i' as string]: i }}>
                  {word}
                  {i < headlineWords.length - 1 ? ' ' : ''}
                </span>
              ))}
            </h1>

            <div className="mt-5 h-px w-16 origin-left bg-gold draw-rule" aria-hidden />

            <p
              className="hero-rise-slow mt-5 max-w-xl font-sans text-base leading-relaxed text-graphit/80 sm:text-lg"
              style={{ animationDelay: '500ms' }}
            >
              {t.heroSub}
            </p>

            <form
              onSubmit={handleSubmit}
              className="hero-rise mt-6"
              style={{ animationDelay: '750ms' }}
            >
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  spellCheck={false}
                  aria-label={t.heroUrlInputLabel}
                  placeholder={t.heroPlaceholder}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="flex-1 rounded-card border border-navy/15 bg-white px-4 py-3.5 font-sans text-base text-graphit placeholder-graphit/40 outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/30"
                />
                <button
                  ref={ctaRef}
                  type="submit"
                  className="group relative rounded-card bg-navy px-6 py-3.5 font-sans text-base font-medium text-creme will-change-transform hover:bg-navy/90"
                  style={{ transition: 'transform 400ms cubic-bezier(0.22, 1, 0.36, 1), background-color 200ms ease' }}
                >
                  <span className="relative z-10">{t.primaryCTA}</span>
                  <span
                    className="absolute inset-0 rounded-card opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    style={{ boxShadow: '0 0 0 1px rgba(201,162,75,0.4), 0 0 28px rgba(201,162,75,0.25)' }}
                    aria-hidden
                  />
                </button>
              </div>
              {/* Trust note — immediately under the input, never far from the CTA. */}
              <p className="mt-2.5 font-sans text-[12px] text-graphit/65">
                {trustNote(locale)}
              </p>
              {/* Anki signal — single line, gold-deep eyebrow register. The
                  concrete killer feature that separates us from the generic
                  YouTube-summarizer category. Placed under the trust note so
                  it lands the moment a hesitant visitor would scan for
                  reasons. */}
              <p className="mt-1 font-sans text-[12px] italic text-gold-deep">
                {ankiSignal(locale)}
              </p>
              {error && (
                <p role="alert" className="mt-2 font-sans text-sm text-red-700">{error}</p>
              )}
              {/* T4 conversion chip — fires when the paste-form value is a
                  YouTube playlist URL (not a single-video URL). Soft Season-
                  Pack hint: a playlist is the canonical input shape for that
                  Pro Plus feature, and detecting it in the moment of paste
                  is the only place where the suggestion lands as helpful
                  rather than promotional. The chip dismisses + persists per
                  conversionTriggers.ts. */}
              {isPlaylistUrl(value) && (
                <div className="mt-3">
                  <ConversionChip
                    triggerId={TRIGGERS.LONG_VIDEO_OR_PLAYLIST}
                    locale={locale}
                    analyticsProps={{ source: 'hero_paste' }}
                  />
                </div>
              )}
            </form>

            <div
              className="hero-rise mt-4 flex flex-wrap gap-x-6 gap-y-2 font-sans text-sm"
              style={{ animationDelay: '900ms' }}
            >
              <button
                type="button"
                onClick={scrollToHow}
                className="text-graphit/65 underline-offset-4 transition hover:text-navy hover:underline"
              >
                {t.seeHowCTA} →
              </button>
              <Link
                to="/pack/sample"
                className="text-graphit/65 italic underline-offset-4 transition hover:text-gold hover:underline"
              >
                {t.trySamplePack}
              </Link>
            </div>
          </div>

          {/* Right — live interactive Sample Pack preview */}
          <div className="animate-fade-in" style={{ animationDelay: '300ms' }}>
            <HeroPackPreview />
          </div>
        </div>
      </div>
    </section>
  );
}

// "Free to try. No signup. Start instantly." — localised under the paste input so
// the trust signal arrives at the moment of hesitation. The added
// "no signup" is the most-asked unspoken question for a visitor about
// to paste a URL into a tool they've never used; surfacing it
// pre-emptively closes the Lovable-style growth-hack loop (deliver
// value first, signup only when the user wants to back it up).
// Takes locale from useLocale's reactive state, never from DOM (DOM
// reads are not React-reactive and caused mixed-language UI when the
// user switched picker).
function trustNote(locale: string): string {
  if (locale.startsWith('es')) return 'Gratis para probar. Sin registro. Listo al instante.';
  if (locale.startsWith('pt')) return 'Grátis para experimentar. Sem registo. Pronto num instante.';
  if (locale.startsWith('de')) return 'Kostenlos testen. Ohne Anmeldung. Sofort startklar.';
  return 'Free to try. No signup. Start instantly.';
}

// Anki signal — surfaced as a single italic line beneath the trust note.
// YouTube → Anki is the most concrete differentiator we have versus the
// generic YouTube-summarizer category (Eightify, NoteGPT, Recall, etc.
// all do summaries; none ship a real .apkg deck per video). Naming it
// out loud at the moment of hesitation moves more first-paste conversions
// than any other line on this page.
function ankiSignal(locale: string): string {
  if (locale.startsWith('es')) return 'Cualquier vídeo, mazo Anki en un clic.';
  if (locale.startsWith('pt')) return 'Qualquer vídeo, deck Anki num clique.';
  if (locale.startsWith('de')) return 'Jedes Video, Anki-Deck in einem Klick.';
  return 'Any video, Anki deck in one click.';
}
